import { AfterViewInit, Directive, inject, Input } from "@angular/core";
import { Deck, DeckCard } from "@apiserver-rest/ponder-data-client-base";
import { DeckGroup } from "./deck-group";
import { DeckBaseComponent } from "../deck-base.component";
import { DeckLayerComponent } from "../deck-layer.component";
import { DeckMultiRowComponent } from "../deck-multi-row.component";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { debounceTime, Subscription } from "rxjs";
import { getDeckCardName } from "../../utils";

/**
 * A group of {@link DeckMultiRowComponent}s
 */
@Directive()
export class DeckGroupComponent implements AfterViewInit {
    @Input({ required: true }) public isOwnDeck!: boolean;
    @Input({ required: true }) public group!: DeckGroup;
    @Input({ required: true }) public deck!: Deck;
    @Input({ required: true }) public deckComponent!: DeckBaseComponent;
    @Input({ required: true }) public deckLayerComponent!: DeckLayerComponent;

    protected events: EventBroker = inject(EventBroker);
    protected itemVisibilitySub!: Subscription;

    /**
     * The logical representation of a row of card(s).
     * Not to be confused by the actual component refs in {@link deckCardRowComponentRefs}.
     * 
     * Each row can contain one or more cards, where each card have the same name.
     */
    public deckCardRows: DeckCardRow[] = [];

    /**
     * All of the deck multi-row components in the group.
     */
    private deckCardRowComponentRefs: DeckMultiRowComponent[] = []

    /**
     * Is at least one row of this group visible?
     */
    public isVisible: boolean = true;

    // -------------------------------------------------------------------------------------
    // Life Cycle
    // -------------------------------------------------------------------------------------

    public constructor() {
        this.itemVisibilitySub = this.events.ui.itemVisibilityRecalculatedEvent
            .pipe(debounceTime(100))
            .subscribe({
                next: () => {
                    this.onItemVisibilityUpdated();
                }
            });
    }

    public ngOnInit(): void {
        this.group.cards.forEach(c => { this.addToRow(c); });
        this.onItemVisibilityUpdated();
    }
    
    public ngAfterViewInit(): void {
        this.deckLayerComponent.registerGroupComponent(this);
    }

    public ngOnDestroy() {
        this.itemVisibilitySub.unsubscribe();
    }

    // -------------------------------------------------------------------------------------
    // Events
    // -------------------------------------------------------------------------------------

    private onItemVisibilityUpdated(): void {
        this.updateAllRowVisibility();
        this.updateGroupVisibility();
        this.sortRows();
    }
    
    // -------------------------------------------------------------------------------------
    // DeckMultiRowComponent Parentage
    // -------------------------------------------------------------------------------------

    /**
     * Invoked by {@link DeckMultiRowComponent} to link back to this parent.
     * @param deckRowComponent 
     */
    public registerRowComponent(deckRowComponent: DeckMultiRowComponent): void {
        this.deckCardRowComponentRefs.push(deckRowComponent);
    }

    public unregisterRowComponent(deckRowComponent: DeckMultiRowComponent): void {
        for (let i = 0; i < this.deckCardRowComponentRefs.length; ++i) {
            if (this.deckCardRowComponentRefs[i] === deckRowComponent) {
                this.deckCardRowComponentRefs.splice(i, 1);
                return;
            }
        }
    }

    /**
     * Fetches the component ref that the specified card is represented in.
     * @param deckCard
     * @returns 
     */
    protected getMultiRowComponent(deckCard: DeckCard | undefined): DeckMultiRowComponent | undefined {
        if (!deckCard) {
            return undefined;
        }

        for (let i = 0; i < this.deckCardRowComponentRefs.length; ++i) {
            if (this.deckCardRowComponentRefs[i].matches(deckCard)) { 
                return this.deckCardRowComponentRefs[i];
            }
        }

        return undefined;
    }
    
    // -------------------------------------------------------------------------------------
    // Add / Remove / Get Cards and Rows
    // -------------------------------------------------------------------------------------
    
    /**
     * Adds the card to this group.
     * @param deckCard
     */
    public addCard(deckCard: DeckCard): void {
        this.addToRow(deckCard);
        this.onItemVisibilityUpdated();

        this.deckComponent.cdr.detectChanges();
    }

    /**
     * Removes the specified card from this card group.
     * @param deckCard
     */
    public removeCard(deckCard: DeckCard): void {
        this.removeFromRow(deckCard);
        this.getMultiRowComponent(deckCard)?.recalculateTotals();
        this.onItemVisibilityUpdated();
    }

    /**
     * Adds the card to the group (row) that has the same name.
     * If no such row exists, a new row is added.
     * @param card
     */
    private addToRow(deckCard: DeckCard): void {
        var name = getDeckCardName(deckCard);
        var row = this.getRow(name);
        var cardVisible = this.deckComponent.filteredItems.isCardVisible(deckCard);

        if (row) {
            row.cards.push(deckCard);
            row.visible = row.visible || cardVisible;
        } else {
            this.deckCardRows.push({
                name: name,
                cards: [deckCard],
                visible: cardVisible
            });
        }
        
        var multirow = this.getMultiRowComponent(deckCard);
        multirow?.addCard(deckCard);
    }

    /**
     * Removes the card from its row. 
     * If the row is empty as a result, that row is then removed as well.
     * @param card 
     */
    private removeFromRow(deckCard: DeckCard): void {
        var name = getDeckCardName(deckCard);
        var row = this.getRow(name);

        if (row) {
            var index = row.cards.findIndex(dc => {
                return (dc.deckCardId == deckCard.deckCardId);
            });

            // Remove the card from the row grouping
            if (index >= 0) {
                row.cards.splice(index, 1);
            }

            // If the row grouping is not empty, update the visibility.
            if (row.cards.length != 0) {
                this.updateRowVisibility(row!);
            } else {
                this.removeRow(row);
            }
        }
    }

    /**
     * Retrieves the row with the specified name.
     * @param name
     * @returns 
     */
    private getRow(name: string): DeckCardRow | undefined {
        for (let i = 0; i < this.deckCardRows.length; ++i) {
            if (this.deckCardRows[i].name === name) {
                return this.deckCardRows[i];
            }
        }

        return undefined;
    }

    private removeRow(row: DeckCardRow): void {
        for (let i = 0; i < this.deckCardRows.length; ++i) {
            if (this.deckCardRows[i] == row) {
                this.deckCardRows.splice(i, 1);
                return;
            }
        }
    }

    /**
     * Sorts the rows.
     * 
     * Technically these are already sorted in the {@link FilteredDeckPreviewPreviewItems}
     * but since we are directly referencing the underlying {@link Deck} we need to carry
     * over that sorting to here.
     */
    private sortRows(): void {
        this.deckCardRows.sort((a: DeckCardRow, b: DeckCardRow) => {
            var indexA = this.deckComponent.filteredItems.getCurrentIndex(a.cards[0].card);
            var indexB = this.deckComponent.filteredItems.getCurrentIndex(b.cards[0].card);

            return (indexA - indexB);
        });
    }

    /**
     * Sets the row visible if at least one of its cards is visible.
     */
    private updateRowVisibility(row: DeckCardRow): void {
        row.visible = false;

        for (let i = 0; i < row.cards.length; ++i) {
            if (this.deckComponent.filteredItems.isCardVisible(row.cards[i])) {
                row.visible = true;
                return;
            }
        }
    }

    /**
     * Updates visibility for all rows.
     */
    private updateAllRowVisibility(): void {
        this.deckCardRows.forEach(r => {
            this.updateRowVisibility(r);
        });
    }

    /**
     * Sets the group as visible if at least one row is visible.
     */
    private updateGroupVisibility(): void {
        this.isVisible = false;

        for (let i = 0; i < this.deckCardRows.length; ++i) {
            if (this.deckCardRows[i].visible) {
                this.isVisible = true;
                return;
            }
        }
    }
}

/**
 * Grouping of one of more cards with the same name.
 * For example, can have one or more distinct cards with the same name of "Forest".
 * 
 * Used to populate multi-row components.
 */
type DeckCardRow = {
    /**
     * Name of all cards in this row.
     */
    name: string,

    /**
     * The card(s) in this row.
     */
    cards: DeckCard[],

    /**
     * Is at least one card in this row visible?
     * Cards with the same name can still have different filter results.
     * 
     * Example: filter on artist or by card text for set.
     */
    visible: boolean
}

